/**
 * Layer builder (blocks/omi-layer-builder.liquid), after the Lifekind Trio builder.
 *
 * Every opening starts from the standard build. The layer panels fill in as the customer moves through
 * the steps; firmness is described in words. "Add to bag" adds the selected size with the three layer
 * properties, refreshes the cart through the theme's cart event and hands over to the add-on dialog,
 * which listens for source "omi-layer-builder".
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const STEP_NAMES = ['Top layer', 'Middle layer', 'Base layer', 'Review your build'];

class OmiLayerBuilder extends HTMLElement {
  /** @type {any} */ S = {};
  step = 1;

  connectedCallback() {
    this.dialog = /** @type {HTMLDialogElement} */ (this.querySelector('dialog'));
    try {
      this.defaults = JSON.parse(this.dataset.defaults || '{}');
    } catch {
      this.defaults = {};
    }
    this.addEventListener('click', this.#onClick);
    this.dialog.addEventListener('close', () => this.opener?.focus?.());
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });
    document.addEventListener('shopify:product:select', () => requestAnimationFrame(() => this.#applySize()));
  }

  get section() {
    return this.closest('.shopify-section');
  }

  /** The variant the product form will add */
  variantId() {
    const input = /** @type {HTMLInputElement | null} */ (
      this.section?.querySelector('product-form-component form[data-type="add-to-cart-form"] input[name="id"], product-form-component input[name="id"]')
    );
    return input?.value || new URL(window.location.href).searchParams.get('variant') || '';
  }

  sizeName() {
    const checked = /** @type {HTMLInputElement | null} */ (this.section?.querySelector('variant-picker fieldset input:checked'));
    return checked?.value || '';
  }

  splitEligible() {
    const size = this.sizeName().toLowerCase();
    return !String(this.dataset.noSplit || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
      .includes(size);
  }

  open() {
    this.S = { ...this.defaults, split: false };
    this.opener = /** @type {HTMLElement | null} */ (document.activeElement);
    const add = /** @type {HTMLButtonElement} */ (this.querySelector('[data-omi-layers-add]'));
    add.disabled = false;
    add.textContent = 'Add to bag';
    /** @type {HTMLElement} */ (this.querySelector('[data-omi-error]')).hidden = true;
    this.#applySize();
    this.goStep(1);
    this.dialog?.showModal();
    /** @type {HTMLElement | null} */ (this.querySelector('.omi-layers__heading'))?.focus?.();
  }

  #applySize() {
    const eligible = this.splitEligible();
    const row = /** @type {HTMLElement | null} */ (this.querySelector('[data-omi-split-row]'));
    if (row) row.hidden = !eligible;
    if (!eligible && this.S.split) this.S.split = false;
    this.#paint();
  }

  /** @param {number} n */
  goStep(n) {
    this.step = n;
    for (const el of this.querySelectorAll('[data-omi-step]')) {
      /** @type {HTMLElement} */ (el).hidden = Number(/** @type {HTMLElement} */ (el).dataset.omiStep) !== n;
    }
    const prog = /** @type {HTMLElement} */ (this.querySelector('[data-omi-progress]'));
    [...prog.children].forEach((seg, i) => seg.classList.toggle('is-done', i < n));
    prog.setAttribute('aria-valuenow', String(n));
    prog.setAttribute('aria-label', `Step ${n} of 4`);
    const live = this.querySelector('[data-omi-live]');
    if (live) live.textContent = `Step ${n} of 4: ${STEP_NAMES[n - 1]}`;
    this.#paint();
  }

  describe() {
    const S = this.S;
    return {
      top: `${S.topFirm} · ${S.topSurf}`,
      mid: S.split ? `Left: ${S.midLeft} / Right: ${S.midRight}` : S.midFirm,
      base: S.baseFirm,
    };
  }

  #paint() {
    const S = this.S;
    if (!S.topFirm) return;
    for (const group of this.querySelectorAll('[data-omi-group]')) {
      const key = /** @type {HTMLElement} */ (group).dataset.omiGroup || '';
      for (const b of group.querySelectorAll('button')) {
        const on = /** @type {HTMLElement} */ (b).dataset.v === S[key];
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', String(on));
      }
    }
    const toggle = /** @type {HTMLElement | null} */ (this.querySelector('[data-omi-split]'));
    if (toggle) {
      toggle.textContent = S.split ? 'On' : 'Off';
      toggle.setAttribute('aria-pressed', String(!!S.split));
    }
    const single = /** @type {HTMLElement | null} */ (this.querySelector('[data-omi-mid="single"]'));
    const split = /** @type {HTMLElement | null} */ (this.querySelector('[data-omi-mid="split"]'));
    if (single) single.hidden = !!S.split;
    if (split) split.hidden = !S.split;

    // Layer panels fill in as the steps are reached
    const d = this.describe();
    const panels = /** @type {const} */ ([
      ['top', 1, S.topSurf],
      ['mid', 2, S.split ? 'split' : 'single'],
      ['base', 3, 'base'],
    ]);
    for (const [key, reachedAt, image] of panels) {
      const panel = /** @type {HTMLElement} */ (this.querySelector(`[data-omi-panel="${key}"]`));
      const reached = this.step >= reachedAt;
      panel.classList.toggle('is-pending', !reached);
      for (const img of panel.querySelectorAll('[data-omi-img]')) {
        /** @type {HTMLElement} */ (img).hidden = !reached || /** @type {HTMLElement} */ (img).dataset.omiImg !== image;
      }
      const detail = /** @type {HTMLElement} */ (panel.querySelector('[data-omi-detail]'));
      detail.textContent = reached ? d[key] : 'Not yet chosen';
    }
    const left = this.querySelector('[data-omi-side="left"]');
    const right = this.querySelector('[data-omi-side="right"]');
    if (left) left.textContent = S.midLeft;
    if (right) right.textContent = S.midRight;

    for (const key of ['top', 'mid', 'base']) {
      const el = this.querySelector(`[data-omi-confirm="${key}"]`);
      if (el) el.textContent = d[/** @type {'top'|'mid'|'base'} */ (key)];
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const t = /** @type {HTMLElement} */ (event.target);
    if (t.closest('[data-omi-layers-open]')) return this.open();
    if (t.closest('[data-omi-layers-close]')) return this.dialog?.close();
    const choice = /** @type {HTMLElement | null} */ (t.closest('[data-omi-group] button'));
    if (choice) {
      const key = /** @type {HTMLElement} */ (choice.closest('[data-omi-group]')).dataset.omiGroup || '';
      this.S[key] = choice.dataset.v;
      return this.#paint();
    }
    if (t.closest('[data-omi-split]')) {
      if (!this.splitEligible()) return;
      this.S.split = !this.S.split;
      return this.#paint();
    }
    const go = /** @type {HTMLElement | null} */ (t.closest('[data-omi-goto]'));
    if (go) return this.goStep(Number(go.dataset.omiGoto));
    if (t.closest('[data-omi-layers-add]')) this.#addToBag();
  };

  async #addToBag() {
    const button = /** @type {HTMLButtonElement} */ (this.querySelector('[data-omi-layers-add]'));
    const error = /** @type {HTMLElement} */ (this.querySelector('[data-omi-error]'));
    const id = this.variantId();
    if (!id) {
      error.textContent = 'Please choose a size first.';
      error.hidden = false;
      return;
    }
    button.disabled = true;
    button.textContent = 'Adding…';
    error.hidden = true;
    const d = this.describe();
    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1, properties: { 'Top layer': d.top, 'Middle layer': d.mid, 'Base layer': d.base } }],
          sections: sectionIds.join(','),
        }),
      });
      const json = await response.json();
      if (!response.ok || json.status) throw new Error(json.description || json.message || 'Could not add to your bag.');
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      this.dialog?.close();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          sections: json.sections,
          source: 'omi-layer-builder',
          productId: this.dataset.productId,
          didError: false,
        },
      });
    } catch (e) {
      deferred.reject(e);
      error.textContent = /** @type {Error} */ (e).message;
      error.hidden = false;
      button.disabled = false;
      button.textContent = 'Add to bag';
    }
  }
}

if (!customElements.get('omi-layer-builder')) customElements.define('omi-layer-builder', OmiLayerBuilder);
